import React from "react";
import { useTranslation } from "react-i18next";
import PrivacyIcon from "../../../../assets/images/png/privacy_icon.png";
import { Image } from "../../../../components/__common/Image";
import { Typography } from "../../../../components/__common/Typography/Typography";
import {
  LinkButton,
  TTSButton,
} from "../../../../components/__common/_controls";
import { Svg, SVG_ICONS } from "../../../../components/__common/Svg/Svg";
import {
  EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY,
  EVENT_CATEGORY_PRIVACY_POLICY,
} from "../../../../constants/analytics";
import { TabItem } from "../../../../utils/privacyPolicyUtils";
import GoogleAnalytics from "../../../../utils/GoogleAnalytics";
import { PrivacyPolicyTabs } from "./PrivacyPolicyTabs";

interface PrivacyPolicyHeaderProps {
  hasVersionHistory: boolean;
  ppSource: string;
  onVersionHistoryClick: () => void;
  onLanguageClick: () => void;
  shouldShowTabs: boolean;
  tabItem: TabItem[];
  activeTab: string;
  onTabChange: (tabName: string) => void;
}

/**
 * PrivacyPolicyHeader component
 * Displays the title section with action buttons
 */
export const PrivacyPolicyHeader: React.FC<PrivacyPolicyHeaderProps> = ({
  hasVersionHistory,
  ppSource,
  onVersionHistoryClick,
  onLanguageClick,
  shouldShowTabs,
  tabItem,
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();

  const handleDownloadClick = () => {
    GoogleAnalytics.triggerEvent(
      EVENT_CATEGORY_PRIVACY_POLICY,
      EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY
    );
  };

  return (
    <div className="privacy-policy__title-section">
      <Image image={PrivacyIcon} width={48} height={48} />
      <Typography variant="h2" className="privacy-policy__title-section__title">
        {t("PRIVACY_POLICY_GPP_PAGE_TITLE")}
        <TTSButton value={t("PRIVACY_POLICY_GPP_PAGE_TITLE")} />
      </Typography>

      <PrivacyPolicyTabs
        shouldShowTabs={shouldShowTabs}
        tabItem={tabItem}
        activeTab={activeTab}
        onTabChange={onTabChange}
      />

      <div className="privacy-policy__title-section__buttons">
        {hasVersionHistory && (
          <LinkButton
            id="version-history-button"
            testId="toggle-version-history-modal"
            to="#"
            textClassName="privacy-policy__title-section__buttons__version-history"
            title={t("PRIVACY_POLICY_GPP_BTN_VERSION_HISTORY")}
            icon={SVG_ICONS.PRIVACY_POLICY_HISTORY}
            iconPosition="left"
            iconSize={24}
            underlined={false}
            onClick={onVersionHistoryClick}
            svgIconToolTip={t("PRIVACY_POLICY_GPP_VERSION_HISTORY_POPUP_TITLE")}
          />
        )}

        <LinkButton
          id="policy-language-button"
          to="#"
          testId="pp-gpp-button-change-lang"
          textClassName="privacy-policy__title-section__buttons__language"
          title={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
          icon={SVG_ICONS.PRIVACY_POLICY_LANGUAGE}
          iconPosition="left"
          iconSize={24}
          underlined={false}
          onClick={onLanguageClick}
          svgIconToolTip={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
        />

        <a
          data-testid="trigger-download-button"
          download
          href={ppSource}
          className="privacy-policy__title-section__buttons__download"
          onClick={handleDownloadClick}
        >
          <Svg
            icon={SVG_ICONS.PRIVACY_POLICY_DOWNLOAD}
            width={24}
            height={24}
            className="privacy-policy__title-section__buttons__download__icon"
            title={t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
          />
          <Typography variant="body2" weight="w700">
            {t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
          </Typography>
        </a>
      </div>
    </div>
  );
};
